import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { api } from '../../../lib/api-client';
import {
  WS_EVENTS,
  type LivingSummaryBlocks,
  type LivingSummaryUpdatedPayload,
} from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

export interface LivingSummaryState {
  status: 'idle' | 'loading' | 'ready' | 'updating' | 'error';
  blocks: LivingSummaryBlocks | null;
  version: number;
  sourceMessagesCount: number;
  generatedAt: string | null;
  /** Regeneração sob demanda em curso (operador clicou "Atualizar resumo"). */
  regenerating: boolean;
  /** Mensagem honesta de falha do refresh manual (ex.: 409 summary-agent disabled). */
  regenerateError: string | null;
  /** Dispara a regeneração manual; o resultado chega pelo WS `living-summary.updated`. */
  requestRegenerate: () => Promise<void>;
}

/**
 * useLivingSummary — Fatia 8.
 *
 * Carrega Resumo Vivo da conversa via REST + subscribe WS para updates.
 * Estado `updating` aparece brevemente quando WS event chega mas refetch
 * ainda não trouxe os blocos novos (shimmer no painel lateral).
 */
export function useLivingSummary(conversationId: string | undefined): LivingSummaryState {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();
  const [updating, setUpdating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);

  const queryKey = ['living-summary', conversationId];
  const queryResult = useQuery({
    queryKey,
    enabled: conversationId !== undefined,
    queryFn: () => {
      if (conversationId === undefined) return Promise.resolve(null);
      return api.getLivingSummary(conversationId);
    },
  });

  useEffect(() => {
    if (conversationId === undefined) return;
    const unsub = subscribe<LivingSummaryUpdatedPayload>(
      WS_EVENTS.LIVING_SUMMARY_UPDATED,
      (envelope) => {
        if (envelope.data.conversationId !== conversationId) return;
        setUpdating(true);
        void queryClient.invalidateQueries({ queryKey }).finally(() => setUpdating(false));
      },
    );
    return () => unsub();
  }, [conversationId, queryClient, subscribe, queryKey]);

  /**
   * Regeneração sob demanda. Otimista: acende `regenerating` imediatamente.
   * O backend `await`a o `regenerate()` antes de responder, e esse `regenerate()`
   * emite o WS `living-summary.updated` que invalida a query — então, ao resolver
   * o POST, os blocos novos já vêm no próximo refetch. Em 409 (summary-agent
   * desligado) → mensagem honesta; nunca regenera no escuro.
   */
  const requestRegenerate = useCallback(async (): Promise<void> => {
    if (conversationId === undefined) return;
    setRegenerating(true);
    setRegenerateError(null);
    try {
      await api.regenerateLivingSummary(conversationId);
    } catch (err) {
      const msg =
        err instanceof Error && err.message.length > 0
          ? err.message
          : 'Não foi possível atualizar o resumo agora.';
      setRegenerateError(msg);
    } finally {
      setRegenerating(false);
    }
  }, [conversationId]);

  const data = queryResult.data;
  let status: LivingSummaryState['status'];
  if (queryResult.isError) status = 'error';
  else if (updating || regenerating) status = 'updating';
  else if (queryResult.isLoading) status = 'loading';
  else if (data) status = 'ready';
  else status = 'idle';

  return {
    status,
    blocks: data?.blocks ?? null,
    version: data?.version ?? 0,
    sourceMessagesCount: data?.sourceMessagesCount ?? 0,
    generatedAt: data?.generatedAt ?? null,
    regenerating,
    regenerateError,
    requestRegenerate,
  };
}
